import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PaginationParams, PaginationResponse } from 'src/app/Theme/shared/components/pagination/pagination.interface';
import { PaginationService } from 'src/app/Theme/shared/components/pagination/pagination.service';
import { environment } from 'src/environments/environment';
export interface SortParams {
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface PaginationParamsWithSort {
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  // Optional search parameter (matches admin CF search key)
  item?: string;
}

/**
 * Published items (publish=true) with optional specification and post–price-book price filters.
 * Same pagination response shape as {@link ProductService.getAllProductsList}.
 *
 * Query params: `item`, `sort_by` / `sort_order` (only field: `item`),
 * `item_specs` (comma-separated AND): bare template `itemSpec` ids and/or
 * `<identifier>|<url-encoded line value>` where `<identifier>` is `itemSpec`, `specByItem_pk`, or
 * `name_en_key` for item-only specs (canonical value prefers `en`), `price_min`, `price_max` (optional).
 */
export interface PublishedItemsListParams extends PaginationParamsWithSort {
  item_specs?: string;
  price_min?: number;
  price_max?: number;
}
@Injectable({
  providedIn: 'root'
})

export class ProductService {
  private host = environment.apiUrlHost;

  constructor(private http: HttpClient, private paginationService: PaginationService) { }

  public getAllProducts(): Observable<any> {
    return this.http.get(`${this.host}/CF/all-item-details/`)
  }
  public getAllProductsList(params: PaginationParamsWithSort = {}): Observable<any> {
    return this.paginationService.getPaginatedData(`${this.host}/CF/all-item-list/`, params)
  }

  /**
   * Published catalog list with spec and effective-price filters (aligns with apply_pricebook_pricing).
   * Backend path must match your deployment (default mirrors `all-item-list` naming).
   */
  public getPublishedItemsList(params: PublishedItemsListParams = {}): Observable<any> {
    return this.paginationService.getPaginatedData(`${this.host}/CF/filtered-publish-list/`, params)
  }

  /** Catalog max price for customer filter slider (`GET` with no query params). */
  public getFilteredPublishListMaxPrice(): Observable<unknown> {
    return this.http.get(`${this.host}/CF/filtered-publish-list/max-price/`);
  }

  public getAllPVideos(): Observable<any> {
    return this.http.get(`${this.host}/CF/videos/`)
  }

  public getNewProduct(): Observable<any> {
    return this.http.get(`${this.host}/CF/last-published/`)
  }


  public getLastMonthPublishedProduct(params: PaginationParams = {}): Observable<PaginationResponse<any>> {
    return this.paginationService.getPaginatedData<any>(
      `${this.host}/CF/last-month-published/`, 
      params
    );
  }
  /**
   * Fetches product details by product ID.
   * @param productId - The ID of the product to fetch details for.
   * @returns An Observable containing the product details.
   */
  public getProductById(productId: string): Observable<any> {
    const query1 = new HttpParams()
      .set('itemCode', productId);
    return this.http.get(`${this.host}/CF/item-details/`, { params: query1 });

  }

  /** Live on-hand quantity for one item. GET CF/on-hand-stock/<item>/ */
  public getOnHandStock(item: string): Observable<{ item: string; site: string; onHandStock: number }> {
    return this.http.get<{ item: string; site: string; onHandStock: number }>(
      `${this.host}/CF/on-hand-stock/${encodeURIComponent(item)}/`
    );
  }
  // Method to save the basket (POST)
  addToList(favoriteList: any): Observable<any> {

    return this.http.post(`${this.host}/CP/favorites/`, favoriteList, { withCredentials: true });
  }

  removeFromList(itemCode: string): Observable<any> {
    return this.http.delete(`${this.host}/CP/favorites/${itemCode}/`, { withCredentials: true });
  }

  public getFavoriteList(params: PaginationParams = {}): Observable<any> {

    return this.paginationService.getPaginatedData<any>(`${this.host}/CP/favorites/`, params);
  }


  // Updated getFavoriteList to support backend pagination
  public getFavoriteList2(page: number = 1, page_size: number = 5, filter: string = 'all', search: string = ''): Observable<any> {
    const params: any = {
      page: page,
      page_size: page_size
    };

    if (filter && filter !== 'all') {
      params.filter = filter; // backend must support `?filter=yesterday|lastWeek|lastMonth`
    }

    if (search && search.trim()) {
      params.query = search; // backend uses `?query=search_term`
    }

    return this.paginationService.getPaginatedData<any>(`${this.host}/CP/favorites/`, params);
  }



  public getProductsPublic(params: PaginationParamsWithSort = {}): Observable<any> {
    const searchParams: PaginationParamsWithSort = {
      page: params.page || 1,
      page_size: params.page_size || 8,
      sort_by: params.sort_by || 'item',
      sort_order: params.sort_order || 'asc',
      ...params
    };
    
    console.log('API call with params:', searchParams);
    
    return this.paginationService.getPaginatedData<any>(`${this.host}/CF/allItemsHome/`, searchParams);
  }
  
  public getDetailsProductsPublic(itemCode: string): Observable<any> {
    return this.http.get(`${this.host}/CF/itemDetailsHome/${itemCode}/`)
  }
}
